import { FC, useEffect } from 'react';
import { ICountryExchangeDetail } from '../../../../../types/countries';
import { IcountryPricingData, INewCountryPricingFormData, IProductDetailId } from '../../../../../types/products';
import { PricingDataByCountry } from './Components';
import { AddCountry } from '../../../../countriesList/components';
import PricingForm from './Components/PricingForm/PricingForm';
import useCatalogueState from '../../../../../state/useCatalogueState';
import { normalizeNumericValue } from '../../../../../utils/pricing.utils';
import { IPendingStoreStatusChange } from '../../FormProduct';
import './Pricing.scss';

interface IProductForm {
  numberTab: number;
  productDataDetail?: IProductDetailId;
  countriesOpenForSale?: ICountryExchangeDetail[];
  setVerifyPricing: (areVerified: boolean) => void;
  setChangePricing: (change: boolean) => void;
  setProductDataDetail?: (data: IProductDetailId) => void;
  addPendingStoreStatusChange?: (change: IPendingStoreStatusChange) => void;
}

const Pricing: FC<IProductForm> = ({
  numberTab,
  productDataDetail,
  countriesOpenForSale,
  setVerifyPricing,
  setChangePricing,
  setProductDataDetail,
  addPendingStoreStatusChange
}: IProductForm) => {
  const productPricingDataByCountry = productDataDetail?.pricingData;
  const hasCountryData =
    productPricingDataByCountry && productPricingDataByCountry.length > 0 && !!productPricingDataByCountry[0];
  const { showPricingForm, setShowPricingForm } = useCatalogueState();

  const handlePricingFormChange = () => {
    setVerifyPricing(true);
    setChangePricing(true);
  };

  const handleAddNewCountryToPricing = (newCountryData: INewCountryPricingFormData, isQuoteProduct: boolean) => {
    if (!productDataDetail || !setProductDataDetail) return;

    const hasCompletePricing = !!newCountryData?.providerPrice && !!newCountryData?.margin;
    const shouldBeActive = hasCompletePricing && !isQuoteProduct;

    const newPricingCountry: IcountryPricingData = {
      availableProviders: newCountryData?.availableProviders,
      name: newCountryData?.countryData?.name || '',
      nameCode: newCountryData?.countryData?.code || '',
      flag: newCountryData?.countryData?.flag || '',
      currencyCode: newCountryData?.countryData?.currencyCode,
      currency: newCountryData?.countryData?.currencyCode,
      providerId: newCountryData?.providerId || null,
      isActive: shouldBeActive ? 1 : 0,
      inStock: 1,
      isQuoteProduct: isQuoteProduct,
      taxRate: normalizeNumericValue(newCountryData?.taxRate),
      exchangeRate: normalizeNumericValue(newCountryData?.exchangeRate),
      providerPrice: normalizeNumericValue(newCountryData?.providerPrice),
      margin: normalizeNumericValue(newCountryData?.margin),
      nudosPriceUsd: normalizeNumericValue(newCountryData?.nudosPriceUsd),
      nudosPrice: normalizeNumericValue(newCountryData?.nudosPrice),
      nudosPriceUsdWithTaxes: normalizeNumericValue(newCountryData?.nudosPriceUsdWithTaxes),
      nudosPriceWithTaxes: normalizeNumericValue(newCountryData?.nudosPriceWithTaxes)
    };

    setProductDataDetail({
      ...productDataDetail,
      pricingData: [...(productDataDetail?.pricingData || []), newPricingCountry]
    });

    if (productDataDetail?.productId && newCountryData?.countryData?.code) {
      addPendingStoreStatusChange?.({
        countryCode: newCountryData.countryData.code,
        isActive: shouldBeActive,
        isQuoteProduct
      });
    }

    setChangePricing(true);
  };

  useEffect(() => {
    handlePricingFormChange();

    //eslint-disable-next-line
  }, [showPricingForm]);

  return (
    <section className="pricingTabSection" style={{ display: numberTab !== 3 ? 'none' : '' }}>
      {hasCountryData &&
        productPricingDataByCountry.map(countryData => {
          return (
            <PricingDataByCountry
              key={`countryData-${countryData?.name}`}
              currencyCode={countryData?.currency || countryData?.currencyCode || ''}
              flag={countryData?.flag}
              countryName={countryData?.name}
              preloadedProviderPrice={countryData?.providerPrice ? +countryData?.providerPrice : 0}
              preloadedMargin={countryData?.margin ? +countryData?.margin : 0}
              preloadedStatus={countryData?.productStatus || ''}
              preloadedExchangeRate={+countryData?.exchangeRate}
              preloadedTaxRate={countryData?.taxRate ? +countryData?.taxRate : 0}
              preloadedNudosPriceUsdWithTaxes={
                countryData?.nudosPriceUsdWithTaxes ? +countryData?.nudosPriceUsdWithTaxes : 0
              }
              preloadedNudosPriceUsd={countryData?.nudosPriceUsd ? +countryData?.nudosPriceUsd : 0}
              id={countryData.id}
              productId={productDataDetail?.productId}
              countryNameCode={countryData?.nameCode}
              setVerifyPricing={setVerifyPricing}
              setChangePricing={setChangePricing}
              setProductDataDetail={setProductDataDetail}
              countriesOpenForSale={countriesOpenForSale}
              availableProviders={countryData.availableProviders}
              providerId={countryData.providerId || null}
              pricingisActive={Boolean(countryData.isActive)}
              isQuoteProduct={Boolean(countryData?.isQuoteProduct)}
              addPendingStoreStatusChange={addPendingStoreStatusChange}
            />
          );
        })}

      {!showPricingForm && <AddCountry action={() => setShowPricingForm(!showPricingForm)} className="mt-40" />}
      {showPricingForm && (
        <PricingForm
          showPricingForm={showPricingForm}
          setShowPricingForm={setShowPricingForm}
          setVerifyPricing={setVerifyPricing}
          productDataDetail={productDataDetail}
          onAddCountryToPricing={handleAddNewCountryToPricing}
        />
      )}
    </section>
  );
};

export default Pricing;
